import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer } from "./element-renderer"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
} from "../components/ui/pagination"
import { cn } from "../lib/utils"

export function PaginationRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  // Note: child buttons use variant === "previous"/"next" as a legacy
  // role marker (see conditional chain below); the atom-defaults contract
  // applies to the outer <Pagination>, which is orthogonal.
  const atomDefaults = useAtomDefaults("pagination")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  const items = element.children ?? []

  return (
    <Pagination
      className={cn(element.className)}
      data-testid={`pagination-${element.id}`}
      data-atom-type="pagination"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    >
      <PaginationContent>
        {items.map((child) => (
          <PaginationItem key={child.id}>
            {child.type === "button" ? (
              child.variant === "previous" ? (
                <PaginationPrevious
                  text={child.text ? String(resolveSymbol(child.text) ?? "Previous") : "Previous"}
                  onClick={() => dispatchEvent(child.id, "tap", { value: child.text ?? child.id })}
                />
              ) : child.variant === "next" ? (
                <PaginationNext
                  text={child.text ? String(resolveSymbol(child.text) ?? "Next") : "Next"}
                  onClick={() => dispatchEvent(child.id, "tap", { value: child.text ?? child.id })}
                />
              ) : (
                <PaginationLink
                  isActive={child.variant === "default"}
                  onClick={() => dispatchEvent(child.id, "tap", { value: child.text ?? child.id })}
                  aria-label={child.ariaLabel}
                >
                  {child.text ? String(resolveSymbol(child.text) ?? "") : ""}
                </PaginationLink>
              )
            ) : (
              <ElementRenderer element={child} />
            )}
          </PaginationItem>
        ))}
      </PaginationContent>
    </Pagination>
  )
}
